import React, { useEffect, createElement } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ErrorBoundary from './components/ui/ErrorBoundary';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import Services from './pages/Services';
import MentalCapacity from './pages/Services/MentalCapacity';
import TraumaPTSD from './pages/Services/TraumaPTSD';
import Specialist from './pages/Services/Specialist';
import Privacy from './pages/Policies/Privacy';
import Terms from './pages/Policies/Terms';
import Cancellation from './pages/Policies/Cancellation';
import ForensicRisk from './pages/Services/ForensicRisk';
import Neurodevelopmental from './pages/Services/Neurodevelopmental';
import ClinicalNegligence from './pages/Services/ClinicalNegligence';
import Sitemap from './pages/Sitemap';
// Google Analytics 4 - inject directly into DOM so Google can detect it
function GoogleAnalytics() {
  useEffect(() => {
    // Only inject once
    if (document.getElementById('gtag-script')) return;
    const gtagScript = document.createElement('script');
    gtagScript.id = 'gtag-script';
    gtagScript.async = true;
    gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-HT5Z5GWWNY';
    document.head.insertBefore(gtagScript, document.head.firstChild);
    const inlineScript = document.createElement('script');
    inlineScript.id = 'gtag-inline';
    inlineScript.textContent = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-HT5Z5GWWNY');
    `;
    document.head.insertBefore(inlineScript, gtagScript.nextSibling);
  }, []);
  return null;
}
// ScrollToTop component to ensure page loads at the top
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
// Skip to content link for accessibility
function SkipToContent() {
  return <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-secondary focus:text-white focus:rounded-md focus:shadow-lg" data-id="element-1">
      Skip to main content
    </a>;
}
export function AppRouter() {
  return <BrowserRouter data-id="element-2">
      <Helmet data-id="element-3">
        <title data-id="element-4">
          Medicolegal Psychiatry – Expert Witness Psychiatric Services UK
        </title>
        <meta name="description" content="Dr. Imtiaz Syed provides expert psychiatric assessments and reports for legal proceedings. Trusted by solicitors, barristers and courts across the UK." data-id="element-5" />
        {/* Open Graph tags */}
        <meta property="og:type" content="website" data-id="element-6" />
        <meta property="og:title" content="Medicolegal Psychiatry – Expert Witness Psychiatric Services UK" data-id="element-7" />
        <meta property="og:description" content="Independent medico-legal psychiatry led by Dr. Imtiaz Syed. Expert witness reports, trauma, capacity, ADHD, PTSD & forensic assessments UK-wide." data-id="element-8" />
        <meta property="og:url" content="https://medicolegal-psychiatry.co.uk" data-id="element-9" />
        <meta property="og:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/u9Nr778koLscH9uY6kZjEn/01._Hero_Image.png" data-id="element-10" />
        <meta property="og:site_name" content="Medicolegal Psychiatry" data-id="element-11" />
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" data-id="element-12" />
        <meta name="twitter:title" content="Medicolegal Psychiatry – Expert Witness Psychiatric Services UK" data-id="element-13" />
        <meta name="twitter:description" content="Independent medico-legal psychiatry led by Dr. Imtiaz Syed. Expert witness reports, trauma, capacity, ADHD, PTSD & forensic assessments UK-wide." data-id="element-14" />
        <meta name="twitter:image" content="https://uploadthingy.s3.us-west-1.amazonaws.com/u9Nr778koLscH9uY6kZjEn/01._Hero_Image.png" data-id="element-15" />
        {/* Canonical URL */}
        <link rel="canonical" href="https://medicolegal-psychiatry.co.uk" data-id="element-16" />
        <link rel="preconnect" href="https://fonts.googleapis.com" data-id="element-17" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" data-id="element-18" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Merriweather:wght@400;700&display=swap" rel="stylesheet" data-id="element-19" />
        <link rel="icon" href="https://uploadthingy.s3.us-west-1.amazonaws.com/hDXV1rDXM1m2ARqkQ9Hg8F/Medico_Favicon.png" type="image/png" data-id="element-20" />
        <script type="application/ld+json" data-id="element-21">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "Medicolegal Psychiatry",
              "url": "https://medicolegal-psychiatry.co.uk",
              "logo": "https://uploadthingy.s3.us-west-1.amazonaws.com/hDXV1rDXM1m2ARqkQ9Hg8F/Medico_Favicon.png",
              "description": "Independent medico-legal psychiatry led by Dr. Imtiaz Syed. Expert witness reports, trauma, capacity, ADHD, PTSD & forensic assessments UK-wide.",
              "email": "enquiries@medicolegal-psychiatry.co.uk",
              "founder": {
                "@type": "Person",
                "name": "Dr. Imtiaz Syed",
                "jobTitle": "Psychiatrist & Expert Witness"
              },
              "sameAs": [
                "https://www.linkedin.com/in/dr-imtiaz-syed/",
                "https://www.facebook.com/MedicoLegal.Psychiatry/",
                "https://www.instagram.com/medicolegal_psychiatry/"
              ]
            }
          `}
        </script>
      </Helmet>
      <GoogleAnalytics data-id="element-22" />
      <ErrorBoundary data-id="element-23">
        <SkipToContent data-id="element-24" />
        <div className="flex flex-col min-h-screen" data-id="element-25">
          <Header data-id="element-26" />
          <ScrollToTop data-id="element-27" />
          <main id="main-content" className="flex-grow" data-id="element-28">
            <Routes data-id="element-29">
              <Route path="/" element={<Home data-id="element-31" />} data-id="element-30" />
              <Route path="/about" element={<About data-id="element-33" />} data-id="element-32" />
              <Route path="/services" element={<Services data-id="element-35" />} data-id="element-34" />
              <Route path="/services/mental-capacity" element={<MentalCapacity data-id="element-37" />} data-id="element-36" />
              <Route path="/services/trauma-ptsd" element={<TraumaPTSD data-id="element-39" />} data-id="element-38" />
              <Route path="/services/specialist" element={<Specialist data-id="element-41" />} data-id="element-40" />
              <Route path="/services/forensic-risk" element={<ForensicRisk data-id="element-43" />} data-id="element-42" />
              <Route path="/services/neurodevelopmental" element={<Neurodevelopmental data-id="element-45" />} data-id="element-44" />
              <Route path="/services/clinical-negligence" element={<ClinicalNegligence data-id="element-47" />} data-id="element-46" />
              <Route path="/sitemap" element={<Sitemap data-id="element-49" />} data-id="element-48" />
              <Route path="/faq" element={<FAQ data-id="element-51" />} data-id="element-50" />
              <Route path="/contact" element={<Contact data-id="element-53" />} data-id="element-52" />
              <Route path="/policies/privacy" element={<Privacy data-id="element-55" />} data-id="element-54" />
              <Route path="/policies/terms" element={<Terms data-id="element-57" />} data-id="element-56" />
              <Route path="/policies/cancellation" element={<Cancellation data-id="element-59" />} data-id="element-58" />
            </Routes>
          </main>
          <Footer data-id="element-60" />
        </div>
      </ErrorBoundary>
    </BrowserRouter>;
}